/**
 * Auth state machine – single source of truth for auth phase and validation policy.
 * See AUTH_DESIGN_AND_HARDENING.md (Implementation progress, steps 2 and 5).
 *
 * This module centralizes:
 * - Computation of auth phase from raw state (no scattered booleans in UI).
 * - Policy for when to run session validation (grace period after creation).
 */

/** Auth phase for UI and flow control; keep in sync with AuthPhase in lib/auth.tsx */
export type AuthPhase =
  | 'initial'
  | 'loading'
  | 'callback'
  | 'creating_session'
  | 'authenticated'
  | 'expired'
  | 'error';

/** Grace period (ms) after session creation/restoration before we run validation that can clear the session. */
export const VALIDATION_GRACE_MS = 60 * 1000;

export interface AuthPhaseInput {
  pathname: string;
  user: unknown;
  session: { sessionId?: string } | null;
  currentTenant: { tenantId?: string } | null;
  error: Error | null;
  isLoading: boolean;
}

/**
 * Compute the current auth phase from raw state. Single place for phase derivation.
 * Use this in the provider so UI and effects key off the same phase.
 */
export function computeAuthPhase(input: AuthPhaseInput): AuthPhase {
  const { pathname, user, session, currentTenant, error, isLoading } = input;
  const isCallbackRoute =
    pathname === '/auth/callback' || pathname === '/auth/silent-callback';
  if (isCallbackRoute) return 'callback';
  // Aegis session + current tenant is enough for the app shell. OIDC `user` may be
  // briefly (or persistently) missing after a hard refresh while localStorage session
  // + sessionStorage authToken restore succeeds — requiring `user` caused false logouts.
  const authenticated = !!(session?.sessionId && currentTenant?.tenantId);
  if (authenticated) return 'authenticated';
  const expired = !!(error && (!session || !session.sessionId));
  if (expired) return 'expired';
  if (error) return 'error';
  if (user && !session) return 'creating_session';
  if (isLoading) return 'loading';
  return 'initial';
}

/**
 * Whether we should run session validation (on-mount or periodic).
 * Returns false when we are still inside the grace period after session creation/restoration,
 * to avoid "create → validate → 403 → clear → create again" loops.
 */
export function getShouldRunValidation(
  sessionId: string | undefined,
  sessionCreatedAtMs: number
): boolean {
  if (!sessionId) return false;
  if (sessionCreatedAtMs <= 0) return true;
  return Date.now() - sessionCreatedAtMs >= VALIDATION_GRACE_MS;
}
